import { createContext, useContext } from 'react';

export type ArenaLang = 'de' | 'en';

const translations = {
  de: {
    // Landing
    'landing.subtitle': 'Mensch oder Maschine — welcher Text begeistert dich mehr?',
    'landing.description':
      'Dir werden 5 Textpaare gezeigt — eines von einem Menschen geschrieben, eines von einer KI. Wähle den Text, der dir besser gefällt. Danach erfährst du, wer was geschrieben hat.',
    'landing.start': "Los geht's",
    'landing.modeLiteratur': 'Literatur',
    'landing.modeSachtext': 'Sachtext',
    'landing.switchToSachtext': 'lieber Sachtext?',
    'landing.switchToLiteratur': 'lieber Literatur?',
    'landing.modePrefix': 'Modus',
    'landing.experiment': 'Ein Experiment von',

    // Arena
    'arena.round': 'Runde',
    'arena.of': 'von',
    'arena.question': 'Welcher Text gefällt dir besser?',
    'arena.loading': 'Lade nächste Runde...',

    // Reveal
    'reveal.humanChosen': 'Dein Favorit wurde von einem Menschen geschrieben',
    'reveal.aiChosen': 'Dein Favorit wurde von {model} geschrieben',
    'reveal.explanation': 'Hier die Auflösung:',
    'reveal.human': 'Mensch',
    'reveal.ai': 'KI',
    'reveal.generatedBy': 'Generiert von',
    'reveal.viewResults': 'Ergebnis ansehen',
    'reveal.next': 'Weiter →',

    // Results
    'results.title': 'Dein Ergebnis',
    'results.human': 'Mensch',
    'results.ai': 'KI',
    'results.msg4ai': 'Die KI-Texte haben dich begeistert — faszinierend, oder?',
    'results.msg3ai': 'Du hast oft den KI-Text bevorzugt. Die Grenzen verschwimmen!',
    'results.msg2ai': 'Eine gute Mischung — Mensch und Maschine können beide überzeugen.',
    'results.msgDefault':
      'Du stehst auf den menschlichen Touch — klassische Literatur begeistert dich.',
    'results.playAgain': 'Nochmal spielen',
    'results.emailHint': 'Erfahre als Erste/r, wenn neue Modelle in der Arena antreten:',
    'results.hermesCta':
      'Fasziniert davon, was KI beim Schreiben leisten kann? Mit <strong>Hermes 3000</strong> schreibst du dein eigenes Buch — mit KI-Unterstützung.',
    'results.hermesButton': 'Hermes 3000 entdecken',

    // Leaderboard
    'leaderboard.title': 'Leaderboard',
    'leaderboard.loading': 'Lade Leaderboard...',
    'leaderboard.all': 'Gesamt',
    'leaderboard.allVotes': 'Alle Stimmen',
    'leaderboard.votes': 'Stimmen',
    'leaderboard.totalVotes': '{count} Stimmen insgesamt',
    'leaderboard.popularity': 'Beliebtheit = Wie oft wurde der KI-Text bevorzugt',
    'leaderboard.authorPopularity':
      'Präferenz-Quote = Wie oft hat der menschliche Text besser gefallen',
    'leaderboard.tabModels': 'KI-Modelle',
    'leaderboard.tabAuthors': 'Autoren',
    'leaderboard.noData': 'Noch keine Daten vorhanden. Spiel eine Runde!',
    'leaderboard.back': '← Zurück',

    // Share
    'share.text':
      'Ich habe in der Text Arena {human}x den Menschen und {ai}x die KI bevorzugt. Mensch oder Maschine — welcher Text begeistert dich mehr?',
    'share.x': 'Auf X teilen',
    'share.whatsapp': 'Per WhatsApp teilen',
    'share.linkedin': 'Auf LinkedIn teilen',
    'share.copy': 'Link kopieren',

    // Email
    'email.placeholder': 'E-Mail-Adresse',
    'email.submit': 'Dabei',
    'email.thanks': 'Danke! Wir halten dich auf dem Laufenden.',

    // Legal
    'legal.imprint': 'Impressum',
    'legal.privacy': 'Datenschutz',
  },
  en: {
    // Landing
    'landing.subtitle': 'Human or machine — which text captivates you more?',
    'landing.description':
      "You'll see 5 text pairs — one written by a human, one by an AI. Pick the text you prefer. Then find out who wrote what.",
    'landing.start': "Let's go",
    'landing.modeLiteratur': 'Literature',
    'landing.modeSachtext': 'Non-fiction',
    'landing.switchToSachtext': 'prefer non-fiction?',
    'landing.switchToLiteratur': 'prefer literature?',
    'landing.modePrefix': 'Mode',
    'landing.experiment': 'An experiment by',

    // Arena
    'arena.round': 'Round',
    'arena.of': 'of',
    'arena.question': 'Which text do you prefer?',
    'arena.loading': 'Loading next round...',

    // Reveal
    'reveal.humanChosen': 'Your favorite was written by a human',
    'reveal.aiChosen': 'Your favorite was written by {model}',
    'reveal.explanation': 'Here is the reveal:',
    'reveal.human': 'Human',
    'reveal.ai': 'AI',
    'reveal.generatedBy': 'Generated by',
    'reveal.viewResults': 'View results',
    'reveal.next': 'Next →',

    // Results
    'results.title': 'Your result',
    'results.human': 'Human',
    'results.ai': 'AI',
    'results.msg4ai': 'The AI texts captivated you — fascinating, right?',
    'results.msg3ai': 'You often preferred the AI text. The lines are blurring!',
    'results.msg2ai': 'A good mix — both human and machine can be convincing.',
    'results.msgDefault': "You're drawn to the human touch — classic literature speaks to you.",
    'results.playAgain': 'Play again',
    'results.emailHint': 'Be the first to know when new models enter the arena:',
    'results.hermesCta':
      'Fascinated by what AI can do for writing? With <strong>Hermes 3000</strong> you can write your own book — with AI assistance.',
    'results.hermesButton': 'Discover Hermes 3000',

    // Leaderboard
    'leaderboard.title': 'Leaderboard',
    'leaderboard.loading': 'Loading leaderboard...',
    'leaderboard.all': 'All',
    'leaderboard.allVotes': 'All votes',
    'leaderboard.votes': 'votes',
    'leaderboard.totalVotes': '{count} votes total',
    'leaderboard.popularity': 'Popularity = How often the AI text was preferred',
    'leaderboard.authorPopularity': 'Preference rate = How often the human text was preferred',
    'leaderboard.tabModels': 'AI Models',
    'leaderboard.tabAuthors': 'Authors',
    'leaderboard.noData': 'No data yet. Play a round first!',
    'leaderboard.back': '← Back',

    // Share
    'share.text':
      'In the Text Arena I preferred the human {human}x and the AI {ai}x. Human or machine — which text captivates you more?',
    'share.x': 'Share on X',
    'share.whatsapp': 'Share on WhatsApp',
    'share.linkedin': 'Share on LinkedIn',
    'share.copy': 'Copy link',

    // Email
    'email.placeholder': 'Email address',
    'email.submit': "I'm in",
    'email.thanks': "Thanks! We'll keep you posted.",

    // Legal
    'legal.imprint': 'Imprint',
    'legal.privacy': 'Privacy',
  },
} as const;

type TranslationKey = keyof (typeof translations)['de'];

export const LangContext = createContext<ArenaLang>('de');

export function useLang(): ArenaLang {
  return useContext(LangContext);
}

export function useT() {
  const lang = useContext(LangContext);
  return (key: TranslationKey, vars?: Record<string, string | number>) => {
    let text: string = translations[lang][key] || translations['de'][key] || key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        text = text.replace(`{${k}}`, String(v));
      }
    }
    return text;
  };
}
